import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TimerCompletePayload,
  TimerDurationsInput,
  TimerMode,
  TimerQuestionChangePayload,
  TimerState,
  WRITING_ANSWER_SECONDS,
  getDurationMsForMode,
  resolveTimerDurations,
} from '../const/timer';

interface UseChgkTimerOptions {
  initialMode?: TimerMode;
  durations?: TimerDurationsInput;
  onModeChange?: (mode: TimerMode) => void;
  onTimerComplete?: (payload: TimerCompletePayload) => void;
  onQuestionChange?: (payload: TimerQuestionChangePayload) => void;
  onStateChange?: (state: TimerState) => void;
}

const TICK_MS = 50;
const WRITING_ANSWER_MS = WRITING_ANSWER_SECONDS * 1000;
const SPEED_MULTIPLIER =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 3
    : 1;

const isActiveState = (state: TimerState) =>
  state === TimerState.RUNNING || state === TimerState.WRITING_ANSWER;

export const useChgkTimer = (options: UseChgkTimerOptions = {}) => {
  const {
    initialMode = TimerMode.QUESTION,
    durations,
    onModeChange,
    onTimerComplete,
    onQuestionChange,
    onStateChange,
  } = options;

  const resolvedDurations = resolveTimerDurations(durations);

  const [mode, setMode] = useState<TimerMode>(initialMode);
  const [state, setState] = useState<TimerState>(TimerState.IDLE);
  const [questionPhase, setQuestionPhase] = useState<'main' | 'writing'>('main');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [dupletStep, setDupletStep] = useState(1);
  const [blitzStep, setBlitzStep] = useState(1);
  const [remainingMs, setRemainingMs] = useState(() => getDurationMsForMode(initialMode, resolvedDurations));

  const intervalRef = useRef<number | null>(null);
  const endTimeRef = useRef<number | null>(null);

  const runtimeRef = useRef({
    mode,
    state,
    questionPhase,
    questionNumber,
    dupletStep,
    blitzStep,
    durations: resolvedDurations,
  });

  useEffect(() => {
    runtimeRef.current = {
      mode,
      state,
      questionPhase,
      questionNumber,
      dupletStep,
      blitzStep,
      durations: resolvedDurations,
    };
  }, [mode, state, questionPhase, questionNumber, dupletStep, blitzStep, resolvedDurations]);

  const modeChangeRef = useRef(onModeChange);
  const timerCompleteRef = useRef(onTimerComplete);
  const questionChangeRef = useRef(onQuestionChange);
  const stateChangeRef = useRef(onStateChange);

  useEffect(() => {
    modeChangeRef.current = onModeChange;
  }, [onModeChange]);

  useEffect(() => {
    timerCompleteRef.current = onTimerComplete;
  }, [onTimerComplete]);

  useEffect(() => {
    questionChangeRef.current = onQuestionChange;
  }, [onQuestionChange]);

  useEffect(() => {
    stateChangeRef.current = onStateChange;
  }, [onStateChange]);

  const emitState = useCallback((next: TimerState) => {
    setState(next);
    stateChangeRef.current?.(next);
  }, []);

  const clearTicker = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const getMainModeDurationMs = useCallback(
    (targetMode: TimerMode) => getDurationMsForMode(targetMode, runtimeRef.current.durations),
    []
  );

  const transitionTo = useCallback(
    (
      nextQuestionNumber: number,
      nextDupletStep: number,
      nextBlitzStep: number,
      reason: TimerQuestionChangePayload['reason']
    ) => {
      setQuestionNumber(nextQuestionNumber);
      setDupletStep(nextDupletStep);
      setBlitzStep(nextBlitzStep);
      questionChangeRef.current?.({
        mode: runtimeRef.current.mode,
        questionNumber: nextQuestionNumber,
        dupletStep: nextDupletStep,
        blitzStep: nextBlitzStep,
        reason,
      });
    },
    []
  );

  const tick = useCallback(() => {
    const target = endTimeRef.current;
    if (!target) return;

    const diff = (target - Date.now()) * SPEED_MULTIPLIER;
    if (diff <= 0) {
      const snapshot = runtimeRef.current;
      clearTicker();
      endTimeRef.current = null;
      setRemainingMs(0);

      if (snapshot.questionPhase === 'main') {
        const isFinalSubStep =
          snapshot.mode === TimerMode.QUESTION ||
          (snapshot.mode === TimerMode.DUPLET && snapshot.dupletStep >= 2) ||
          (snapshot.mode === TimerMode.BLITZ && snapshot.blitzStep >= snapshot.durations.blitzSteps);

        timerCompleteRef.current?.({
          mode: snapshot.mode,
          phase: 'main',
          questionNumber: snapshot.questionNumber,
          dupletStep: snapshot.dupletStep,
          blitzStep: snapshot.blitzStep,
          isFinalBlitzStep: isFinalSubStep,
        });

        if (isFinalSubStep) {
          setQuestionPhase('writing');
          setRemainingMs(WRITING_ANSWER_MS);
          emitState(TimerState.WRITING_ANSWER);
          endTimeRef.current = Date.now() + WRITING_ANSWER_MS / SPEED_MULTIPLIER;
          intervalRef.current = window.setInterval(tick, TICK_MS);
          return;
        }

        emitState(TimerState.FINISHED);
        return;
      }

      timerCompleteRef.current?.({
        mode: snapshot.mode,
        phase: 'writing',
        questionNumber: snapshot.questionNumber,
        dupletStep: snapshot.dupletStep,
        blitzStep: snapshot.blitzStep,
        isFinalBlitzStep: snapshot.mode !== TimerMode.BLITZ || snapshot.blitzStep >= snapshot.durations.blitzSteps,
      });

      emitState(TimerState.FINISHED);
      return;
    }

    setRemainingMs(diff);
  }, [clearTicker, emitState]);

  const start = useCallback(() => {
    if (isActiveState(state)) return;

    const snapshot = runtimeRef.current;
    const restartFromMain =
      state === TimerState.FINISHED &&
      remainingMs <= 0 &&
      snapshot.questionPhase === 'writing';

    if (restartFromMain) {
      setQuestionPhase('main');
    }

    const effectivePhase = restartFromMain ? 'main' : snapshot.questionPhase;
    const baseDuration =
      effectivePhase === 'writing'
        ? WRITING_ANSWER_MS
        : getMainModeDurationMs(snapshot.mode);
    const nextRemaining = remainingMs > 0 ? remainingMs : baseDuration;

    endTimeRef.current = Date.now() + nextRemaining / SPEED_MULTIPLIER;
    setRemainingMs(nextRemaining);
    emitState(
      effectivePhase === 'writing'
        ? TimerState.WRITING_ANSWER
        : TimerState.RUNNING
    );

    clearTicker();
    intervalRef.current = window.setInterval(tick, TICK_MS);
  }, [clearTicker, emitState, getMainModeDurationMs, remainingMs, state, tick]);

  const pause = useCallback(() => {
    if (!isActiveState(state)) return;

    clearTicker();
    if (endTimeRef.current) {
      setRemainingMs(Math.max(0, (endTimeRef.current - Date.now()) * SPEED_MULTIPLIER));
    }
    endTimeRef.current = null;
    emitState(TimerState.PAUSED);
  }, [clearTicker, emitState, state]);

  const toggleStartPause = useCallback(() => {
    if (isActiveState(state)) pause();
    else start();
  }, [pause, start, state]);

  const resetCurrent = useCallback(() => {
    clearTicker();
    endTimeRef.current = null;
    setQuestionPhase('main');

    const currentMode = runtimeRef.current.mode;
    const nextDuration = getMainModeDurationMs(currentMode);
    setRemainingMs(nextDuration);
    emitState(TimerState.IDLE);

    questionChangeRef.current?.({
      mode: currentMode,
      questionNumber: runtimeRef.current.questionNumber,
      dupletStep: runtimeRef.current.dupletStep,
      blitzStep: runtimeRef.current.blitzStep,
      reason: 'reset',
    });
  }, [clearTicker, emitState, getMainModeDurationMs]);

  const nextQuestion = useCallback(() => {
    clearTicker();
    endTimeRef.current = null;
    setQuestionPhase('main');

    const snapshot = runtimeRef.current;
    let nextQuestionNumber = snapshot.questionNumber;
    let nextDupletStep = 1;
    let nextBlitzStep = 1;

    if (snapshot.mode === TimerMode.DUPLET) {
      if (snapshot.dupletStep < 2) nextDupletStep = snapshot.dupletStep + 1;
      else nextQuestionNumber = snapshot.questionNumber + 1;
    } else if (snapshot.mode === TimerMode.BLITZ) {
      if (snapshot.blitzStep < snapshot.durations.blitzSteps) nextBlitzStep = snapshot.blitzStep + 1;
      else nextQuestionNumber = snapshot.questionNumber + 1;
    } else {
      nextQuestionNumber = snapshot.questionNumber + 1;
    }

    transitionTo(nextQuestionNumber, nextDupletStep, nextBlitzStep, 'manual_next');
    setRemainingMs(getMainModeDurationMs(snapshot.mode));
    emitState(TimerState.IDLE);
  }, [clearTicker, emitState, getMainModeDurationMs, transitionTo]);

  const nextAndStart = useCallback(() => {
    clearTicker();
    endTimeRef.current = null;
    setQuestionPhase('main');

    const snapshot = runtimeRef.current;
    let nextQuestionNumber = snapshot.questionNumber;
    let nextDupletStep = 1;
    let nextBlitzStep = 1;

    if (snapshot.mode === TimerMode.DUPLET) {
      if (snapshot.dupletStep < 2) nextDupletStep = snapshot.dupletStep + 1;
      else nextQuestionNumber = snapshot.questionNumber + 1;
    } else if (snapshot.mode === TimerMode.BLITZ) {
      if (snapshot.blitzStep < snapshot.durations.blitzSteps) nextBlitzStep = snapshot.blitzStep + 1;
      else nextQuestionNumber = snapshot.questionNumber + 1;
    } else {
      nextQuestionNumber = snapshot.questionNumber + 1;
    }

    transitionTo(nextQuestionNumber, nextDupletStep, nextBlitzStep, 'manual_next');

    const duration = getMainModeDurationMs(snapshot.mode);
    setRemainingMs(duration);
    endTimeRef.current = Date.now() + duration / SPEED_MULTIPLIER;
    emitState(TimerState.RUNNING);
    intervalRef.current = window.setInterval(tick, TICK_MS);
  }, [clearTicker, emitState, getMainModeDurationMs, tick, transitionTo]);

  const switchMode = useCallback(
    (nextMode: TimerMode) => {
      if (nextMode === runtimeRef.current.mode) return;

      clearTicker();
      endTimeRef.current = null;

      setMode(nextMode);
      setQuestionPhase('main');
      setQuestionNumber(1);
      setDupletStep(1);
      setBlitzStep(1);
      setRemainingMs(getMainModeDurationMs(nextMode));
      emitState(TimerState.IDLE);

      modeChangeRef.current?.(nextMode);
      questionChangeRef.current?.({
        mode: nextMode,
        questionNumber: 1,
        dupletStep: 1,
        blitzStep: 1,
        reason: 'mode_change',
      });
    },
    [clearTicker, emitState, getMainModeDurationMs]
  );

  useEffect(() => {
    return () => {
      clearTicker();
    };
  }, [clearTicker]);

  return {
    mode,
    state,
    questionPhase,
    remainingMs,
    remainingSeconds: Math.max(0, Math.ceil(remainingMs / 1000)),
    questionNumber,
    dupletStep,
    blitzStep,
    dupletStepsTotal: 2,
    blitzStepsTotal: resolvedDurations.blitzSteps,
    durations: resolvedDurations,
    isRunning: isActiveState(state),
    switchMode,
    start,
    pause,
    toggleStartPause,
    resetCurrent,
    nextQuestion,
    nextAndStart,
  };
};
